import React, { useCallback } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useIntl } from "../../../../../../shared/core/i18n/use-intl";
import { clientManager, featuresManager } from "../../../../../../shared/core/service/services";
import { MUST_RETAKE_ONBOARDING_FILE_STATUSES } from "../../../../../../shared/domains/onboarding/onboarding";
import { useOnboardingFile } from "../../../../../../shared/domains/onboarding/use-onboarding";
import { useObservable } from "../../../../../../shared/utils/observable";
import { Path } from "../../../../../core/routing/path";
import { UnstyledButton } from "../../../../common/buttons/default-button";
import { ChevronIcon } from "../../../../common/svg/chevron";
import { StaticAssets } from "../../../../styles/custom-assets";
import { theme } from "../../../../styles/theme";

export const OnboardingFileStatusCard: React.FC = () => {
  const features = useObservable(featuresManager.features);
  const client = useObservable(clientManager.client);
  const { onboardingFile } = useOnboardingFile();
  const { formatMessage } = useIntl();
  const history = useHistory();

  const handleEditOnboardingFile = useCallback(async () => {
    if (features.sdaCustomerOnboardingUpdate) {
      history.push(Path.OnboardingFile);
    }
  }, []);

  const mustRetakeOnboarding = onboardingFile && MUST_RETAKE_ONBOARDING_FILE_STATUSES.includes(onboardingFile.status);

  // requiredActions is either a single object carrying a reason, or a list of KYC actions.
  const requiredActions = client?.requiredActions;
  const reason = requiredActions && !Array.isArray(requiredActions) ? requiredActions.reason : undefined;

  return mustRetakeOnboarding ? (
    <Container disabled={!features.sdaCustomerOnboardingUpdate} onClick={handleEditOnboardingFile}>
      <CardIcon src={StaticAssets.warningCircle} alt="" />
      <Texts>
        {/* The action names the card; what used to be its title explains it underneath. */}
        <Title>{formatMessage("onboardingStatusCard.button")}</Title>
        <Subtitle>{reason || formatMessage("onboardingStatusCard.title")}</Subtitle>
      </Texts>
      <Chevron width={14} height={14} />
    </Container>
  ) : null;
};

/* The whole card is the action now, so the underlined button it used to carry is gone. */
const Container = styled(UnstyledButton)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  width: 100%;
  padding: 16px;
  border-radius: 13px;
  background-color: #eef4fc;
  text-align: left;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const CardIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const Texts = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const Title = styled.div`
  ${theme.mediumText};
  font-size: 16px;
  line-height: 20px;
  color: ${theme.colors.blue[800]};
`;

const Subtitle = styled.div`
  ${theme.text};
  margin-top: 4px;
  font-size: 14px;
  line-height: 19px;
  color: ${theme.colors.gray[800]};
`;

const Chevron = styled(ChevronIcon)`
  flex-shrink: 0;
  margin-top: 3px;
  fill: ${theme.colors.blue[800]};
`;
